import { useUpdateVetterUserMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

/** The signed-in person's own details — the backend lets them edit only themselves. */
export function EditProfileModal({
	open,
	onOpenChange,
	member,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	member: { id: string; firstName: string; lastName: string; phone?: string | null };
	onSuccess: () => void;
}) {
	const [firstName, setFirstName] = useState(member.firstName);
	const [lastName, setLastName] = useState(member.lastName);
	const [phone, setPhone] = useState(member.phone ?? '');
	const [error, setError] = useState<string | null>(null);
	const [update, { loading }] = useUpdateVetterUserMutation();

	const [wasOpen, setWasOpen] = useState(false);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) {
			setFirstName(member.firstName);
			setLastName(member.lastName);
			setPhone(member.phone ?? '');
			setError(null);
		}
	}

	const canSubmit = !!firstName.trim() && !!lastName.trim();

	const handleSubmit = async () => {
		if (!canSubmit || loading) return;
		setError(null);
		try {
			const { data } = await update({
				variables: {
					id: member.id,
					input: {
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						phone: phone.trim() || null,
					},
				},
			});
			const res = data?.updateVetterUser;
			if (!res?.success) {
				setError(res?.message || 'Could not save your details.');
				return;
			}
			toast.success('Your details are saved');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Could not save your details.');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Your details</DialogTitle>
					<DialogDescription>
						Shown to your colleagues and to 05 Creation.
					</DialogDescription>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label>
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										disabled={loading}
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label>
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										disabled={loading}
									/>
								</div>
							</div>
							<div className="flex flex-col gap-1.5">
								<Label>Phone</Label>
								<Input
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									disabled={loading}
								/>
							</div>
							{error && <div className="text-sm text-status-danger">{error}</div>}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
